import CustomAppTypes from '../config/customAppTypes';
import { isEikaDigitalBankBankregnr, normalizeBankregnr } from '../util/bankregnr';
import { ContextReducer } from './context';

type LoadConfig = import('../reducers/context').ContextReducer;

const formatMissingKey = (key: string, localValue: string) =>
    `Mangler dictionary key i sitecore: ${key}: '${localValue}'`;

const stripSuffixes = (isCustomApp: boolean, suffixCurrentApp: string, keys: string[]): string[] => {
    const strippedKeys: string[] = [];
    const otherSuffixes = CustomAppTypes.all()
        .map((appType: string) => `_${appType}`)
        .filter((suffix: string) => suffix !== suffixCurrentApp);

    keys.forEach((key) => {
        if (key.endsWith(suffixCurrentApp)) {
            const endIndex = key.length - suffixCurrentApp.length;
            strippedKeys.push(key.substring(0, endIndex));
        } else {
            let hasSuffix = false;
            otherSuffixes.forEach((suffix: string) => {
                if (key.endsWith(suffix)) {
                    hasSuffix = true;
                    const endIndex = key.length - suffix.length;
                    strippedKeys.push(key.substring(0, endIndex));
                }
            });
            if (!hasSuffix) {
                strippedKeys.push(key);
            }
        }
    });
    return strippedKeys;
};

// const uniqueArray = array => array.filter((key, index, list) => list.lastIndexOf(key) === index);

const transformText = (sitecoreDataText: any, localDataText: any, appType: string, isCustomApp: boolean) => {
    const transformedText: any = {};
    const appErrors: any = [];
    const suffix = `_${appType}`;
    const sitecoreKeys = stripSuffixes(isCustomApp, suffix, Object.keys(sitecoreDataText));
    const allKeys = sitecoreKeys.concat(Object.keys(localDataText));
    const allUniqueKeys = Array.from(new Set(allKeys));

    allUniqueKeys.forEach((defaultKey) => {
        let transformedValue;
        if (isCustomApp) {
            const appSpesifikkOverrideKey = `${defaultKey}${suffix}`;
            transformedValue = sitecoreDataText[appSpesifikkOverrideKey];

            if (transformedValue === undefined) {
                transformedValue = CustomAppTypes.resolveConfig(appType).text[defaultKey];

                if (transformedValue !== undefined) {
                    appErrors.push(formatMissingKey(appSpesifikkOverrideKey, transformedValue));
                }
            }
        }
        if (transformedValue === undefined) {
            transformedValue = sitecoreDataText[defaultKey];

            if (transformedValue === undefined) {
                /* Fallback til lokal konfig */
                transformedValue = localDataText[defaultKey];

                /* Dersom transformedValue er undefined her er det en custom app key som ikke skal være med for denne appen */
                if (transformedValue !== undefined) {
                    appErrors.push(formatMissingKey(defaultKey, transformedValue));
                }
            }
        }

        transformedText[defaultKey] = transformedValue;
    });

    Object.keys(transformedText).forEach((key) => transformedText[key] === undefined && delete transformedText[key]);

    return { text: transformedText, appErrors };
};

const replaceKeyForString = (string: any, keys: any, configText: any) => {
    let replacedString = string;
    keys.forEach((key: any) => {
        const placeHolderCandidateKey = `{${key}}`;
        if (replacedString.indexOf(placeHolderCandidateKey) > -1) {
            replacedString = replacedString.replace(
                placeHolderCandidateKey,
                replaceKeyForString(configText[key], keys, configText),
            );
        }
    });
    return replacedString;
};

const replaceKeyHolders = (configText: any) => {
    const keys = Object.keys(configText);
    const configWithReplacedText: any = {};

    keys.forEach((key) => {
        configWithReplacedText[key] = replaceKeyForString(configText[key], keys, configText);
    });
    return configWithReplacedText;
};

const loadConfig = (
    state: ContextReducer,
    action: {
        payload: {
            config: LoadConfig['config'];
            localConfigData: LoadConfig['localConfigData'];
            appType: LoadConfig['appType'];
        };
    },
): ContextReducer => {
    const { config, localConfigData, appType } = action.payload;
    const normalizedBankregnr = normalizeBankregnr(config.bankregnr);
    const isEikaDigitalBank = isEikaDigitalBankBankregnr(config.bankregnr);

    let transformedTextResult;
    if (config.data.text !== undefined) {
        transformedTextResult = transformText(
            config.data.text,
            localConfigData.text,
            appType,
            appType !== state.defaultAppType,
        );
    } else {
        transformedTextResult = {
            text: localConfigData.text,
            appErrors: ['Sitecore text missing'],
        };
    }

    const finalText = replaceKeyHolders(transformedTextResult.text);

    return {
        ...state,
        config: {
            ...config,
            bankregnr: normalizedBankregnr,
            isEikaDigitalBank,
            data: {
                ...config.data,

                // Config String Paths (With added fallback!)
                calculatorPath: config?.data?.calculatorPath || localConfigData.calculatorPath,
                landingPagePath: config?.data?.landingPagePath || localConfigData.landingPagePath,
                applicationPath: config?.data?.applicationPath || localConfigData.applicationPath,
                completionPathSecuredLoan:
                    config?.data?.completionPathSecuredLoan || localConfigData.completionPathSecuredLoan,
                completionPathCreditLoan:
                    config?.data?.completionPathCreditLoan || localConfigData.completionPathCreditLoan,
                completionPathCreditCard:
                    config?.data?.completionPathCreditCard || localConfigData.completionPathCreditCard,
                completionPathCreditLoanAdvisor:
                    config?.data?.completionPathCreditLoanAdvisor || localConfigData.completionPathCreditLoanAdvisor,

                // Sitecore text/form keys
                text: finalText,
                form: localConfigData.form,
            },
        },
        appType,
        appErrors: transformedTextResult.appErrors,
    };
};

export default loadConfig;
